import { useState, useEffect } from "react"
import {
  Card,
  Button,
  Typography,
  Row,
  Col,
  Form,
  Input,
  Select,
  Tooltip,
  Alert,
  Space,
  Divider,
  Tag,
  message,
} from "antd"
import {
  LinkOutlined,
  EditOutlined,
  InfoCircleOutlined,
  RocketOutlined,
  DeleteOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons"
import { useNavigate } from "react-router-dom"
import ConfirmationPage from "./components/ConfirmationPage"

const { Title, Text, Paragraph } = Typography
const { Option } = Select

const StrategicGoalSetup = () => {
  const [selectedOption, setSelectedOption] = useState(null)
  const [currentStep, setCurrentStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()

  const [goals, setGoals] = useState([])
  const [metrics, setMetrics] = useState([])

  // UI state
  const [showTooltip, setShowTooltip] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const navigate = useNavigate();

  const goalTypes = [
    "Grow Revenue",
    "Reduce Costs",
    "Launch New Product",
    "Improve Operational Efficiency",
    "Expand Market Presence",
    "Increase Retention",
    "Improve Compliance",
    "Secure Funding",
  ]

  const criticalMetrics = [
    "Monthly Revenue",
    "Churn Rate",
    "Customer Acquisition Cost (CAC)",
    "Runway",
    "Burn Rate",
    "NPS / Customer Satisfaction",
    "Conversion Rate",
    "Employee Headcount",
    "Product Downtime",
    "On-Time Delivery",
  ]

  const integrationTools = [
    { name: "Asana", connected: false, icon: "🎯" },
    { name: "Jira", connected: false, icon: "🔧" },
    { name: "ClickUp", connected: false, icon: "✅" },
    { name: "Salesforce", connected: false, icon: "☁️" },
    { name: "Notion", connected: false, icon: "📝" },
    { name: "Confluence", connected: false, icon: "📚" },
    { name: "HubSpot", connected: false, icon: "🚀" },
    { name: "Monday", connected: false, icon: "📊" },
    { name: "Trello", connected: false, icon: "📋" },
    { name: "Linear", connected: false, icon: "📈" },
  ]

  const exampleGoals = [
    "Grow revenue from $400K→$600K by Q4",
    "Keep churn below 5%",
    "Launch MVP of AI feature by Sept",
  ]

  useEffect(() => {
    if (selectedOption === "manual" && goals.length === 0) {
      addGoal()
    }
  }, [selectedOption])

  const handleOptionSelect = (option) => {
    setSelectedOption(option)

    if (option === "connect") {
      setLoading(true)
      setTimeout(() => {
        setLoading(false)
        setCurrentStep(1)
      }, 1000)
    } else if (option === "manual") {
      setCurrentStep(1)
    }
  }

  const addGoal = () => {
    if (goals.length >= 3) {
      message.warning("You can only add up to 3 goals")
      return
    }

    const newGoal = {
      id: Date.now(),
      type: "",
      description: "",
    }
    setGoals([...goals, newGoal])
  }

  const updateGoal = (id, field, value) => {
    setGoals(goals.map((goal) => (goal.id === id ? { ...goal, [field]: value } : goal)))
  }

  const removeGoal = (id) => {
    if (goals.length <= 1) {
      message.warning("You must have at least one goal")
      return
    }
    setGoals(goals.filter((goal) => goal.id !== id))
  }

  // Metric management functions
  const addMetric = () => {
    const newMetric = {
      id: Date.now(),
      name: "",
      target: "",
      threshold: "",
    }
    setMetrics([...metrics, newMetric])
  }

  const updateMetric = (id, field, value) => {
    setMetrics(metrics.map((metric) => (metric.id === id ? { ...metric, [field]: value } : metric)))
  }

  const removeMetric = (id) => {
    setMetrics(metrics.filter((metric) => metric.id !== id))
  }

  // Form submission handler
  const handleManualGoalSubmit = async () => {
    try {
      // Validate goals
      const validGoals = goals.filter((goal) => goal.type && goal.description.trim())
      if (validGoals.length === 0) {
        message.error("Please add at least one complete goal")
        return
      }

      setIsSubmitting(true)

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500))

      setGoals(validGoals)

      message.success("Goals saved successfully!")
      setCurrentStep(2)
    } catch (error) {
      message.error("Failed to save goals. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  // Navigation functions
  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const startOver = () => {
    setSelectedOption(null)
    setCurrentStep(0)
    setGoals([])
    setMetrics([])
    form.resetFields()
  }

  const renderIntroSection = () => (
    <div style={{ textAlign: "center", marginBottom: 40 }}>
      <Title level={2}>
        How should we track your strategic goals?
      </Title>
      <Paragraph style={{ fontSize: 16, color: "#666", maxWidth: 600, margin: "0 auto" }}>
        Radar+ helps you stay aligned by tracking your business objectives. Choose how we should set these up.
      </Paragraph>
    </div>
  )

  const renderOptionCards = () => (
    <Row gutter={24} style={{ marginBottom: 40 }}>
      <Col xs={24} lg={12}>
        <Card
          hoverable
          className={selectedOption === "connect" ? "selected-card" : ""}
          onClick={() => navigate("/home/integrations")}
          style={{
            height: "100%",
            border: selectedOption === "connect" ? "2px solid #1890ff" : "1px solid #d9d9d9",
            opacity: loading && selectedOption !== "connect" ? 0.6 : 1,
          }}
        >
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <LinkOutlined style={{ fontSize: 48, color: "#1890ff", marginBottom: 16 }} />
            <Title level={3}>
              Connect My Tools
              <Tag color="blue" style={{ marginLeft: 8 }}>
                Recommended
              </Tag>
            </Title>
            <Paragraph style={{ color: "#666", marginBottom: 20 }}>
              Pull goals and initiatives from tools like Asana, Notion, Salesforce, and more. Radar+ will keep these in
              sync and surface the most relevant risks and opportunities.
            </Paragraph>

            <Tooltip
              title={
                <div>
                  <div>• Automatically updates as your tools change</div>
                  <div>• Can be manually edited or overridden</div>
                  <div>• Supports parsing OKRs, project goals, and strategic docs</div>
                </div>
              }
              visible={showTooltip}
              onVisibleChange={setShowTooltip}
            >
              <Button
                type="link"
                icon={<InfoCircleOutlined />}
                onClick={(e) => {
                  e.stopPropagation()
                  setShowTooltip(!showTooltip)
                }}
              >
                Key Features
              </Button>
            </Tooltip>

            <div style={{ marginTop: 16 }}>
              <Text strong>Supported Tools:</Text>
              <div style={{ marginTop: 8 }}>
                {integrationTools.slice(0, 8).map((tool) => (
                  <Tag key={tool.name} style={{ margin: 2 }}>
                    {tool.icon} {tool.name}
                  </Tag>
                ))}
              </div>
            </div>

            <Button
              type="primary"
              size="large"
              style={{ marginTop: 20, width: "100%" }}
              icon={<LinkOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                navigate("/home/integrations") // Navigate to integrations page
              }}
            >
              Connect My Tools
            </Button>
          </div>
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card
          hoverable
          className={selectedOption === "manual" ? "selected-card" : ""}
          onClick={() => !loading && handleOptionSelect("manual")}
          style={{
            height: "100%",
            border: selectedOption === "manual" ? "2px solid #1890ff" : "1px solid #d9d9d9",
            opacity: loading && selectedOption !== "manual" ? 0.6 : 1,
          }}
        >
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <EditOutlined style={{ fontSize: 48, color: "#52c41a", marginBottom: 16 }} />
            <Title level={3}>Set My Goals Manually</Title>
            <Paragraph style={{ color: "#666", marginBottom: 20 }}>
              Don't use integrations or prefer full control? Define your top goals manually. Radar+ will align signals
              and recommendations to them—and suggest updates based on your activity over time.
            </Paragraph>

            <div style={{ textAlign: "left", marginTop: 20 }}>
              <Text strong>Example Goals:</Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                {exampleGoals.map((example, index) => (
                  <li key={index} style={{ color: "#666", marginBottom: 4 }}>
                    "{example}"
                  </li>
                ))}
              </ul>
            </div>

            <Button
              type="primary"
              size="large"
              style={{ marginTop: 20, width: "100%" }}
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                handleOptionSelect("manual")
              }}
            >
              Set Goals Manually
            </Button>
          </div>
        </Card>
      </Col>
    </Row>
  )

  const renderManualGoalForm = () => (
    <Card title="Define Your Strategic Goals" style={{ marginBottom: 24 }}>
      <Title level={4}>
        <RocketOutlined style={{ marginRight: 8 }} />
        What are your top 1–3 business goals right now?
      </Title>
      <Paragraph style={{ color: "#666", marginBottom: 24 }}>
        Think about the strategic outcomes you're aiming for this quarter or year.
      </Paragraph>

      {goals.map((goal) => (
        <Card key={goal.id} size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col span={8}>
              <Select
                placeholder="Choose goal type"
                value={goal.type}
                onChange={(value) => updateGoal(goal.id, "type", value)}
                style={{ width: "100%" }}
              >
                {goalTypes.map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={14}>
              <Input
                placeholder='e.g., "Grow MRR from $40K→$60K by December"'
                value={goal.description}
                onChange={(e) => updateGoal(goal.id, "description", e.target.value)}
              />
            </Col>
            <Col span={2}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeGoal(goal.id)}
                disabled={goals.length <= 1}
              />
            </Col>
          </Row>
        </Card>
      ))}

      {goals.length < 3 && (
        <Button type="dashed" onClick={addGoal} style={{ width: "100%", marginBottom: 24 }} icon={<PlusOutlined />}>
          Add Another Goal ({goals.length}/3)
        </Button>
      )}

      <Divider />

      <Title level={4}>
        {/* <TargetOutlined style={{ marginRight: 8 }} /> */}
        Critical Metrics (Optional)
      </Title>
      <Paragraph style={{ color: "#666", marginBottom: 16 }}>
        These are thresholds or guardrails you never want to fall below—or growth targets you need to hit.
        <Text italic> (These will become Radar Priorities)</Text>
      </Paragraph>

      {metrics.length === 0 ? (
        <Button type="dashed" onClick={addMetric} style={{ width: "100%", marginBottom: 16 }} icon={<PlusOutlined />}>
          Add Critical Metric
        </Button>
      ) : (
        metrics.map((metric) => (
          <Card key={metric.id} size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16} align="middle">
              <Col span={8}>
                <Select
                  placeholder="Choose metric"
                  value={metric.name}
                  onChange={(value) => updateMetric(metric.id, "name", value)}
                  style={{ width: "100%" }}
                >
                  {criticalMetrics.map((metricName) => (
                    <Option key={metricName} value={metricName}>
                      {metricName}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={12}>
                <Input
                  placeholder='e.g., "must stay below 5%" or "must hit $500K this quarter"'
                  value={metric.target}
                  onChange={(e) => updateMetric(metric.id, "target", e.target.value)}
                />
              </Col>
              <Col span={2}>
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeMetric(metric.id)} />
              </Col>
            </Row>
          </Card>
        ))
      )}

      {metrics.length > 0 && (
        <Button type="dashed" onClick={addMetric} style={{ width: "100%", marginBottom: 24 }} icon={<PlusOutlined />}>
          Add Another Metric
        </Button>
      )}

      <div style={{ textAlign: "center", marginTop: 32 }}>
        <Space size="large">
          <Button onClick={goBack} icon={<ArrowLeftOutlined />}>
            Back
          </Button>
          <Button type="primary" size="large" onClick={handleManualGoalSubmit} loading={isSubmitting}>
            {isSubmitting ? "Saving Goals..." : "Save My Goals"}
          </Button>
        </Space>
      </div>
    </Card>
  )

  const renderInfoNote = () => (
    <Alert
      message="Smart Goal Suggestions"
      description="If you skip integrations, we'll occasionally recommend new goals based on patterns in your projects, finances, and execution history. You can accept, dismiss, or edit them."
      type="info"
      icon={<InfoCircleOutlined />}
      style={{ marginTop: 32 }}
    />
  )

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 24px" }}>
      <style jsx>{`
        .selected-card {
          box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
        }
      `}</style>


      {currentStep === 0 && (
        <>
          {renderIntroSection()}
          {renderOptionCards()}
          {renderInfoNote()}
        </>
      )}

      {currentStep === 1 && (
        <>
          {selectedOption === "manual" && renderManualGoalForm()}
        </>
      )}

      {currentStep === 2 && <ConfirmationPage startOver={startOver} goals={goals} />}
    </div>
  )
}

export default StrategicGoalSetup
